/** Common IANA timezone identifiers for dropdown selects. */
const TIMEZONES = ["Pacific/Midway", "Pacific/Honolulu", "America/Anchorage", "America/Los_Angeles", "America/Phoenix", "America/Denver", "America/Chicago", "America/New_York", "America/Halifax", "America/St_Johns", "America/Sao_Paulo", "America/Argentina/Buenos_Aires", "Atlantic/South_Georgia", "Atlantic/Azores", "Europe/London", "Europe/Paris", "Europe/Berlin", "Europe/Helsinki", "Africa/Cairo", "Europe/Istanbul", "Asia/Dubai", "Asia/Karachi", "Asia/Kolkata", "Asia/Dhaka", "Asia/Bangkok", "Asia/Shanghai", "Asia/Tokyo", "Asia/Seoul", "Australia/Sydney", "Pacific/Auckland"];

/**
 * Search aliases for each IANA timezone.
 * Includes both DST and standard abbreviations, common names, country/city names,
 * and informal terms so users can find a timezone regardless of the current
 * DST offset or how they phrase the query.
 */
export const TIMEZONE_ALIASES = {
  "Pacific/Midway": ["SST", "UTC-11", "Samoa", "Samoa Standard Time", "Midway", "Midway Island"],
  "Pacific/Honolulu": ["HST", "UTC-10", "Hawaii", "Hawaii Standard Time", "Hawaiian", "Honolulu", "HAST"],
  "America/Anchorage": ["AKST", "AKDT", "UTC-9", "UTC-8", "Alaska", "Alaska Time", "Alaska Standard Time", "Alaska Daylight Time", "Anchorage"],
  "America/Los_Angeles": ["PST", "PDT", "UTC-8", "UTC-7", "Pacific", "Pacific Time", "PT", "Pacific Standard Time", "Pacific Daylight Time", "Los Angeles", "California", "West Coast", "Seattle", "Portland", "Vegas"],
  "America/Phoenix": ["MST", "UTC-7", "Arizona", "Mountain Standard Time", "Arizona Time", "Phoenix", "Tucson", "no DST"],
  "America/Denver": ["MST", "MDT", "UTC-7", "UTC-6", "Mountain", "Mountain Time", "MT", "Mountain Standard Time", "Mountain Daylight Time", "Denver", "Colorado", "Salt Lake City", "Utah"],
  "America/Chicago": ["CST", "CDT", "UTC-6", "UTC-5", "Central", "Central Time", "CT", "Central Standard Time", "Central Daylight Time", "Chicago", "Texas", "Dallas", "Houston", "Kansas City"],
  "America/New_York": ["EST", "EDT", "UTC-5", "UTC-4", "Eastern", "Eastern Time", "ET", "Eastern Standard Time", "Eastern Daylight Time", "New York", "NYC", "East Coast", "Miami", "Atlanta", "Boston", "Philadelphia"],
  "America/Halifax": ["AST", "ADT", "UTC-4", "UTC-3", "Atlantic", "Atlantic Time", "Atlantic Standard Time", "Atlantic Daylight Time", "Halifax", "Nova Scotia"],
  "America/St_Johns": ["NST", "NDT", "UTC-3:30", "UTC-2:30", "Newfoundland", "Newfoundland Standard Time", "Newfoundland Daylight Time", "St Johns", "Saint Johns", "NFDT", "NFST"],
  "America/Sao_Paulo": ["BRT", "BRST", "UTC-3", "UTC-2", "Brasilia", "Brazil", "Brazil Time", "Sao Paulo", "São Paulo", "South America"],
  "America/Argentina/Buenos_Aires": ["ART", "UTC-3", "Argentina", "Argentina Time", "Buenos Aires", "South America"],
  "Atlantic/South_Georgia": ["GST", "SAST", "UTC-2", "South Georgia", "South Georgia Time"],
  "Atlantic/Azores": ["AZOT", "AZOST", "UTC-1", "UTC+0", "Azores", "Azores Standard Time", "Azores Daylight Time", "Portugal"],
  "Europe/London": ["GMT", "BST", "UTC", "UTC+0", "UTC+1", "London", "British", "UK", "England", "Greenwich", "British Standard Time", "British Summer Time", "Greenwich Mean Time", "Ireland", "Dublin"],
  "Europe/Paris": ["CET", "CEST", "UTC+1", "UTC+2", "Paris", "France", "Central European Time", "Central European Standard Time", "Central European Summer Time"],
  "Europe/Berlin": ["CET", "CEST", "UTC+1", "UTC+2", "Berlin", "Germany", "Central European Time", "Central European Standard Time", "Central European Summer Time", "Amsterdam", "Rome", "Madrid"],
  "Europe/Helsinki": ["EET", "EEST", "UTC+2", "UTC+3", "Helsinki", "Finland", "Eastern European Time", "Eastern European Standard Time", "Eastern European Summer Time", "Tallinn", "Riga", "Vilnius", "Kyiv", "Kiev"],
  "Africa/Cairo": ["EET", "UTC+2", "Egypt", "Cairo", "Eastern European", "Eastern African Time", "Egypt Standard Time"],
  "Europe/Istanbul": ["TRT", "UTC+3", "Turkey", "Istanbul", "Turkish Time", "Turkey Time", "Ankara"],
  "Asia/Dubai": ["GST", "UTC+4", "Gulf", "Dubai", "UAE", "United Arab Emirates", "Arabian Standard Time", "Gulf Standard Time", "Abu Dhabi"],
  "Asia/Karachi": ["PKT", "UTC+5", "Pakistan", "Karachi", "Pakistan Standard Time", "Pakistan Time", "Islamabad"],
  "Asia/Kolkata": ["IST", "UTC+5:30", "India", "Kolkata", "Indian Standard Time", "India Time", "Calcutta", "Mumbai", "Delhi", "Chennai", "Bangalore"],
  "Asia/Dhaka": ["BST", "BDT", "UTC+6", "Bangladesh", "Dhaka", "Bangladesh Standard Time", "Bangladesh Time"],
  "Asia/Bangkok": ["ICT", "UTC+7", "Bangkok", "Thailand", "Indochina", "Indochina Time", "Jakarta", "Ho Chi Minh", "Vietnam", "Indonesia"],
  "Asia/Shanghai": ["CST", "UTC+8", "China", "Shanghai", "Beijing", "China Standard Time", "China Time", "Chinese"],
  "Asia/Tokyo": ["JST", "UTC+9", "Japan", "Tokyo", "Japan Standard Time", "Japan Time", "Japanese", "Osaka"],
  "Asia/Seoul": ["KST", "UTC+9", "Korea", "Seoul", "Korean Standard Time", "Korean Time", "South Korea"],
  "Australia/Sydney": ["AEST", "AEDT", "UTC+10", "UTC+11", "Sydney", "Australia", "Australian Eastern", "Australian Eastern Standard Time", "Australian Eastern Daylight Time", "AESST", "Melbourne"],
  "Pacific/Auckland": ["NZST", "NZDT", "UTC+12", "UTC+13", "New Zealand", "Auckland", "New Zealand Standard Time", "New Zealand Daylight Time", "New Zealand Time", "Wellington"]
};

/** Return the short abbreviation for an IANA timezone, e.g. "PST", "CST". */
export function getTimezoneAbbr(tz) {
  return getTimezoneAbbrForDate(tz, new Date());
}

/**
 * Return the short timezone abbreviation for a given IANA timezone and a
 * specific reference date, e.g. "CST" for America/Chicago on 2026-11-12
 * (standard time) vs "CDT" on 2026-07-04 (daylight time).
 *
 * @param tz   IANA timezone identifier, e.g. "America/Chicago"
 * @param date Reference date (JS Date or YYYY-MM-DD string)
 */
export function getTimezoneAbbrForDate(tz, date) {
  try {
    const ref = typeof date === "string" ? new Date(`${date}T12:00:00`) : date;
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "short"
    }).formatToParts(ref);
    return parts.find(p => p.type === "timeZoneName")?.value || "";
  } catch {
    return "";
  }
}

/** Format a timezone IANA id for display, e.g. "America/Los_Angeles (PST)". */
export function formatTimezoneLabel(tz) {
  const abbr = getTimezoneAbbr(tz);
  return abbr ? `${tz} (${abbr})` : tz;
}

/**
 * Like formatTimezoneLabel but uses a specific reference date so the
 * abbreviation reflects DST vs standard time for that date.
 */
export function formatTimezoneLabelForDate(tz, date) {
  const abbr = getTimezoneAbbrForDate(tz, date);
  return abbr ? `${tz} (${abbr})` : tz;
}

/**
 * Returns all searchable terms for a timezone: the IANA id, display label,
 * and every alias defined in TIMEZONE_ALIASES.  Used by filterOptions in
 * Autocomplete components so searches like "PST", "Pacific Standard", or
 * "California" all resolve to America/Los_Angeles regardless of DST state.
 */
export function getTimezoneSearchTerms(tz) {
  return [tz, formatTimezoneLabel(tz), ...(TIMEZONE_ALIASES[tz] ?? [])];
}

/**
 * Returns a short human-readable location name from an IANA timezone string.
 * e.g. "America/Los_Angeles" → "Los Angeles", "Etc/UTC" → "UTC"
 */
export function getTimezoneDisplayName(tz) {
  const city = tz.split("/").pop() ?? tz;
  return city.replace(/_/g, " ");
}

/**
 * Given an HH:MM time string and an IANA source timezone, returns the time
 * converted into the target timezone as a human-readable string
 * (e.g. "3:00 AM"), or null if:
 *  - the source timezone is invalid / missing
 *  - the source and target timezones have the same UTC offset right now
 *
 * @param hhmm       HH:MM string (24-hour), e.g. "14:30"
 * @param sourceTz   IANA timezone the time is stated in, e.g. "Etc/UTC"
 * @param targetTz   IANA timezone to convert to; defaults to the browser's
 *                   local timezone when omitted
 *
 * Uses today's date as the reference so DST is accounted for correctly.
 */
export function convertTimeToUserTz(hhmm, sourceTz, targetTz) {
  return convertTimeToUserTzDetailed(hhmm, sourceTz, targetTz)?.time ?? null;
}
/**
 * Like convertTimeToUserTz but also returns a date label when the converted
 * time falls on a different calendar day than `eventDateStr` (YYYY-MM-DD in
 * the source timezone).
 *
 * Returns null when the source and target timezones share the same UTC offset
 * (no tooltip needed).
 *
 * @param hhmm          HH:MM string (24-hour), e.g. "23:00"
 * @param sourceTz      IANA timezone the event time is stated in
 * @param targetTz      IANA timezone to convert to; defaults to browser local
 * @param eventDateStr  YYYY-MM-DD of the event in the source timezone; used
 *                      to detect cross-midnight conversions
 */
export function convertTimeToUserTzDetailed(hhmm, sourceTz, targetTz, eventDateStr) {
  if (!hhmm || !sourceTz) return null;
  try {
    const [hStr, mStr] = hhmm.split(":");
    const h = parseInt(hStr ?? "0", 10);
    const m = parseInt(mStr ?? "0", 10);
    if (isNaN(h) || isNaN(m)) return null;
    const effectiveTargetTz = targetTz || Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Reference date components in the source timezone.
    // Use the event's date when provided so cross-midnight detection is accurate.
    let year, month, day;
    if (eventDateStr) {
      const parts = eventDateStr.split("-").map(Number);
      year = parts[0] ?? 0;
      month = parts[1] ?? 1;
      day = parts[2] ?? 1;
    } else {
      const now = new Date();
      const dp = new Intl.DateTimeFormat("en-US", {
        timeZone: sourceTz,
        year: "numeric",
        month: "numeric",
        day: "numeric"
      }).formatToParts(now);
      const gp = type => parseInt(dp.find(p => p.type === type)?.value ?? "0", 10);
      year = gp("year");
      month = gp("month");
      day = gp("day");
    }

    // Naive UTC: treat HH:MM on the reference date as if it were UTC
    const naiveUtcMs = Date.UTC(year, month - 1, day, h, m, 0);
    const sourceOffsetMs = utcOffsetMs(sourceTz, new Date(naiveUtcMs));
    const actualUtcMs = naiveUtcMs - sourceOffsetMs;

    // Skip when source and target share the same current offset
    const now = new Date();
    const sourceOffsetAtNow = utcOffsetMs(sourceTz, now);
    const targetOffsetAtNow = utcOffsetMs(effectiveTargetTz, now);
    if (sourceOffsetAtNow === targetOffsetAtNow) return null;
    const actualUtcDate = new Date(actualUtcMs);
    const time = new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: effectiveTargetTz
    }).format(actualUtcDate);

    // Date label — only when an event date was provided and the converted
    // date falls on a different calendar day.
    let dateLabel = null;
    if (eventDateStr) {
      const targetDateStr = new Intl.DateTimeFormat("en-CA", {
        timeZone: effectiveTargetTz,
        year: "numeric",
        month: "2-digit",
        day: "2-digit"
      }).format(actualUtcDate); // produces "YYYY-MM-DD"
      if (targetDateStr !== eventDateStr) {
        dateLabel = new Intl.DateTimeFormat("en-US", {
          timeZone: effectiveTargetTz,
          month: "short",
          day: "numeric"
        }).format(actualUtcDate); // e.g. "Apr 3"
      }
    }
    return {
      time,
      dateLabel
    };
  } catch {
    return null;
  }
}

/**
 * Returns the UTC offset in milliseconds for `tz` at `date`.
 * Positive = tz is ahead of UTC (e.g. UTC+1 → 3_600_000).
 */
function utcOffsetMs(tz, date) {
  try {
    // Format `date` in `tz` and treat those components as if they were UTC.
    // The difference between that "fake UTC" and the real UTC timestamp = offset.
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: false
    }).formatToParts(date);
    const get = type => parseInt(parts.find(p => p.type === type)?.value ?? "0", 10);
    const yr = get("year");
    const mo = get("month");
    const dy = get("day");
    const hr = get("hour") % 24; // guard against midnight shown as 24
    const mn = get("minute");
    const sc = get("second");
    const fakeUtcMs = Date.UTC(yr, mo - 1, dy, hr, mn, sc);
    return fakeUtcMs - date.getTime();
  } catch {
    return 0;
  }
}
export default TIMEZONES;